'use client';

/**
 * PieChart Component
 *
 * Theme-aware pie/donut chart with glassmorphism tooltips.
 * Built on Recharts with design system integration.
 *
 * @example
 * ```tsx
 * <PieChart
 *   data={[
 *     { name: 'Desktop', value: 45 },
 *     { name: 'Mobile', value: 35 },
 *     { name: 'Tablet', value: 20 },
 *   ]}
 *   dataKey="value"
 *   nameKey="name"
 *   donut
 * />
 * ```
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import {
  Cell,
  Legend,
  Pie,
  type PieLabelRenderProps,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';
import { type NeonAccent, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';

// ============================================================================
// Types
// ============================================================================

export type ChartColorKey = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

export interface PieChartDataItem {
  [key: string]: unknown;
}

export interface PieChartProps {
  /**
   * Chart data
   */
  data: PieChartDataItem[];

  /**
   * Data key for values
   */
  dataKey: string;

  /**
   * Data key for names/labels
   */
  nameKey: string;

  /**
   * Chart height
   * @default 300
   */
  height?: number;

  /**
   * Render as donut (hollow center)
   * @default false
   */
  donut?: boolean;

  /**
   * Inner radius for donut (percentage or pixels)
   * @default '60%'
   */
  innerRadius?: number | string;

  /**
   * Outer radius (percentage or pixels)
   * @default '80%'
   */
  outerRadius?: number | string;

  /**
   * Padding angle between segments (degrees)
   * @default 2
   */
  paddingAngle?: number;

  /**
   * Corner radius for segments
   * @default 4
   */
  cornerRadius?: number;

  /**
   * Show legend
   * @default true
   */
  showLegend?: boolean;

  /**
   * Legend position
   * @default 'bottom'
   */
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';

  /**
   * Show tooltip
   * @default true
   */
  showTooltip?: boolean;

  /**
   * Show labels on segments
   * @default false
   */
  showLabels?: boolean;

  /**
   * Label type
   * @default 'percent'
   */
  labelType?: 'name' | 'value' | 'percent';

  /**
   * Optional neon accent for styling
   */
  neonAccent?: NeonAccent;

  /**
   * Custom colors array (hex values)
   * If not provided, uses theme series colors
   */
  colors?: string[];

  /**
   * Enable active segment animation on hover
   * @default true
   */
  activeAnimation?: boolean;

  /**
   * Center content (for donut charts)
   */
  centerContent?: React.ReactNode;

  /**
   * Tooltip value formatter
   */
  tooltipFormatter?: (value: number, name: string) => [string, string];
}

// ============================================================================
// Styled Components
// ============================================================================

interface TooltipContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const TooltipContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TooltipContainerProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonColors = $neonAccent ? neonAccents[$neonAccent] : null;

  return {
    backgroundColor: neonColors ? ($isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)') : glass.background,
    border: `1px solid ${neonColors ? ($isDark ? neonColors.border : neonColors.borderLight) : glass.border}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    backdropFilter: 'blur(12px)',
    boxShadow: $isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
  };
});

const CenterContentWrapper = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  textAlign: 'center',
});

// ============================================================================
// Custom Tooltip
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    name: string;
    payload: {
      fill: string;
      [key: string]: unknown;
    };
  }>;
  isDark: boolean;
  neonAccent?: NeonAccent;
  formatter?: (value: number, name: string) => [string, string];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, isDark, neonAccent, formatter }) => {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  const [formattedValue, formattedName] = formatter
    ? formatter(entry.value, entry.name)
    : [String(entry.value), entry.name];

  return (
    <TooltipContainer $isDark={isDark} $neonAccent={neonAccent}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: entry.payload.fill,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mr: 1,
          }}
        >
          {formattedName}:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    </TooltipContainer>
  );
};

// ============================================================================
// Active Segment Renderer
// ============================================================================

interface ActiveShapeProps {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}

const renderActiveShape = (props: unknown) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props as ActiveShapeProps;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{
          filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25))',
        }}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 10}
        outerRadius={outerRadius + 12}
        fill={fill}
        opacity={0.5}
      />
    </g>
  );
};

// ============================================================================
// Component
// ============================================================================

export const PieChart: React.FC<PieChartProps> = ({
  data,
  dataKey,
  nameKey,
  height = 300,
  donut = false,
  innerRadius = '60%',
  outerRadius = '80%',
  paddingAngle = 2,
  cornerRadius = 4,
  showLegend = true,
  legendPosition = 'bottom',
  showTooltip = true,
  showLabels = false,
  labelType = 'percent',
  neonAccent,
  colors,
  activeAnimation = true,
  centerContent,
  tooltipFormatter,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chartColors = useChartColors();

  // Use custom colors or theme series colors
  const segmentColors = colors || chartColors.series;

  // Get color for segment
  const getColor = (index: number): string => {
    return segmentColors[index % segmentColors.length];
  };

  // Legend layout based on position
  const legendLayout: 'horizontal' | 'vertical' =
    legendPosition === 'left' || legendPosition === 'right' ? 'vertical' : 'horizontal';

  // Custom label renderer
  const renderLabel = (props: PieLabelRenderProps) => {
    const cx = Number(props.cx) || 0;
    const cy = Number(props.cy) || 0;
    const midAngle = props.midAngle ?? 0;
    const innerRadius = Number(props.innerRadius) || 0;
    const outerRadius = Number(props.outerRadius) || 0;
    const percent = props.percent ?? 0;
    const value = props.value ?? 0;
    const name = String(props.name ?? '');

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    let labelText = '';
    switch (labelType) {
      case 'name':
        labelText = name;
        break;
      case 'value':
        labelText = String(value);
        break;
      case 'percent':
      default:
        labelText = `${(percent * 100).toFixed(0)}%`;
        break;
    }

    // Only show label if segment is big enough
    if (percent < 0.05) return null;

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
        style={{
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        }}
      >
        {labelText}
      </text>
    );
  };

  // Recharts v3 shape renderer with isActive support
  const renderShape = (props: unknown & { isActive?: boolean }) => {
    const shapeProps = props as ActiveShapeProps & { isActive?: boolean };
    if (activeAnimation && shapeProps.isActive) {
      return renderActiveShape(shapeProps);
    }
    return (
      <Sector
        cx={shapeProps.cx}
        cy={shapeProps.cy}
        innerRadius={shapeProps.innerRadius}
        outerRadius={shapeProps.outerRadius}
        startAngle={shapeProps.startAngle}
        endAngle={shapeProps.endAngle}
        fill={shapeProps.fill}
      />
    );
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          {/* Tooltip */}
          {showTooltip && (
            <Tooltip content={<CustomTooltip isDark={isDark} neonAccent={neonAccent} formatter={tooltipFormatter} />} />
          )}

          {/* Legend */}
          {showLegend && (
            <Legend
              layout={legendLayout}
              verticalAlign={legendPosition === 'top' || legendPosition === 'bottom' ? legendPosition : 'middle'}
              align={legendPosition === 'left' || legendPosition === 'right' ? legendPosition : 'center'}
              wrapperStyle={{
                paddingTop: legendPosition === 'bottom' ? 16 : 0,
                paddingBottom: legendPosition === 'top' ? 16 : 0,
                paddingLeft: legendPosition === 'right' ? 16 : 0,
                paddingRight: legendPosition === 'left' ? 16 : 0,
              }}
              formatter={(value) => <span style={{ color: chartColors.text }}>{value}</span>}
            />
          )}

          {/* Pie */}
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={donut ? innerRadius : 0}
            outerRadius={outerRadius}
            paddingAngle={paddingAngle}
            cornerRadius={cornerRadius}
            label={showLabels ? renderLabel : undefined}
            labelLine={false}
            shape={activeAnimation ? renderShape : undefined}
          >
            {data.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColor(index)}
                stroke={isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.5)'}
                strokeWidth={1}
              />
            ))}
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>

      {/* Center Content (for donut charts) */}
      {donut && centerContent && <CenterContentWrapper>{centerContent}</CenterContentWrapper>}
    </Box>
  );
};

PieChart.displayName = 'PieChart';

export default PieChart;
